import {useCallback, useState} from 'react';
import {contextMenu} from 'react-contexify';

import type {TrackersStore} from '../../../../stores/Trackers/Trackers.store';
import type {OnMoreIconClick} from '../types';

import {alert} from '../../../../../../../components/alert';

export const COMMENT_CONTEXT_MENU_ID = 'cm.trackers.comment';

type Params = {
  store: TrackersStore;
  t: (key: string) => string;
};

export function useTrackersComment({store, t}: Params) {
  const [commentText, setCommentText] = useState('');
  const [isCommentInputVisible, setCommentInputVisible] = useState(false);

  const onAddCommentClick = useCallback(() => setCommentInputVisible(true), []);

  const onCancelCommentClick = useCallback(() => {
    setCommentInputVisible(false);
    setCommentText('');
  }, []);

  const handleSubmitComment = useCallback(async () => {
    const text = commentText.trim();

    if (!text) return;

    await store.createDiaryEntry({
      content: text,
      type: 'weight_tracker',
    });
    setCommentText('');
    setCommentInputVisible(false);
  }, [commentText, store]);

  const onMoreCommentClick = useCallback(
    (e: Parameters<OnMoreIconClick>[0], entryId: string) => {
      e.event.DOMEvent?.stopPropagation?.();

      if (e.event.DOMEvent) {
        contextMenu.show({
          event: e.event.DOMEvent,
          id: COMMENT_CONTEXT_MENU_ID,
          props: {commentId: entryId},
        });
      }
    },
    [],
  );

  const handleDeleteComment = useCallback(
    async (commentId: string | undefined) => {
      if (!commentId) {
        return;
      }

      const confirmed = await alert.show<boolean>({
        buttons: [
          {
            label: t('shared:yes_delete'),
            onAwaitableClick: resolve => resolve(true),
            scheme: 'primary',
          },
          {
            label: t('shared:no_close'),
            onAwaitableClick: resolve => resolve(false),
            scheme: 'gray',
          },
        ],
        title: t('shared:message.confirm'),
      });

      if (confirmed) {
        await store.deleteDiaryEntry(commentId);
      }
    },
    [store, t],
  );

  const handleOnDeleteCommentItemClick = useCallback(
    (args: {props?: {commentId?: string}}) => {
      contextMenu.hideAll();
      handleDeleteComment(args.props?.commentId);
    },
    [handleDeleteComment],
  );

  return {
    commentText,
    handleOnDeleteCommentItemClick,
    handleSubmitComment,
    isCommentInputVisible,
    onAddCommentClick,
    onCancelCommentClick,
    onMoreCommentClick,
    setCommentText,
  };
}
